// Angular
import { Pipe, PipeTransform } from '@angular/core';

// Third-party
import * as moment from 'moment-timezone';

@Pipe({
  name: 'utcToTimezoneDate',
  standalone: true
})
export class UtcToTimezoneDatePipe implements PipeTransform {

  /**
   * Transforms a UTC date string to a date string in the specified timezone.
   * @param utcDate The UTC date string to convert
   * @param timezoneCode The timezone code (e.g., 'PT', 'ET', 'MT', 'CT') or full timezone name
   * @param format Optional formatting string
   */
  transform(utcDate: string | null | undefined, timezoneCode: string | null | undefined, format?: string): string {
    if (!utcDate || !timezoneCode) {
      return '-';
    }

    try {
      // Map common timezone codes to full timezone names for all US regions
      const timezoneMap: { [key: string]: string } = {
        // Continental US Standard Time Zones
        'PT': 'America/Los_Angeles',        // Pacific Time
        'PST': 'America/Los_Angeles',       // Pacific Standard Time
        'PDT': 'America/Los_Angeles',       // Pacific Daylight Time
        'MT': 'America/Denver',             // Mountain Time
        'MST': 'America/Denver',            // Mountain Standard Time
        'MDT': 'America/Denver',            // Mountain Daylight Time
        'CT': 'America/Chicago',            // Central Time
        'CST': 'America/Chicago',           // Central Standard Time
        'CDT': 'America/Chicago',           // Central Daylight Time
        'ET': 'America/New_York',           // Eastern Time
        'EST': 'America/New_York',          // Eastern Standard Time
        'EDT': 'America/New_York',          // Eastern Daylight Time
        
        // Alaska Time Zone
        'AT': 'America/Anchorage',          // Alaska Time
        'AST': 'America/Anchorage',         // Alaska Standard Time
        'ADT': 'America/Anchorage',         // Alaska Daylight Time
        'AKST': 'America/Anchorage',        // Alaska Standard Time (alternate)
        'AKDT': 'America/Anchorage',        // Alaska Daylight Time (alternate)
        
        // Hawaii Time Zone
        'HT': 'Pacific/Honolulu',           // Hawaii Time
        'HST': 'Pacific/Honolulu',          // Hawaii Standard Time
        'HDT': 'Pacific/Honolulu',          // Hawaii Daylight Time (rare)
        'HAST': 'Pacific/Honolulu',         // Hawaii-Aleutian Standard Time
        'HADT': 'Pacific/Honolulu',         // Hawaii-Aleutian Daylight Time
        
        // US Territories
        'CHST': 'Pacific/Guam',             // Chamorro Standard Time (Guam, Northern Mariana Islands)
        'SST': 'Pacific/Pago_Pago',         // Samoa Standard Time (American Samoa)
        'AST_PR': 'America/Puerto_Rico',    // Atlantic Standard Time (Puerto Rico, US Virgin Islands)
        'ATLANTIC': 'America/Puerto_Rico',   // Atlantic Time (US Territories)
        
        // Arizona (doesn't observe DST except Navajo Nation)
        'MST_AZ': 'America/Phoenix',         // Mountain Standard Time (Arizona)
        'AZ': 'America/Phoenix',             // Arizona Time
        
        // Additional specific locations for clarity
        'EASTERN': 'America/New_York',
        'CENTRAL': 'America/Chicago',
        'MOUNTAIN': 'America/Denver',
        'PACIFIC': 'America/Los_Angeles',
        'ALASKA': 'America/Anchorage',
        'HAWAII': 'Pacific/Honolulu'
      };

      // Use the mapped timezone or the original if not found in map
      const timezone = timezoneMap[timezoneCode.toUpperCase()] || timezoneCode;

      // Parse the UTC date and convert to the specified timezone
      const utcMoment = moment.utc(utcDate);
      const localizedMoment = utcMoment.clone().tz(timezone);
      
      return localizedMoment.format(format ?? 'MM/DD/YYYY H:mm');
    } catch (error) {
      console.warn('Error converting date to timezone:', error);
      return '-';
    }
  }
} 